import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { http, apiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password !== confirm) return setError("Both passwords must match");
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen min-h-dvh place-items-center px-6 py-14">
      <div className="w-full max-w-md fade-up" data-testid="reset-password-page">
        <div className="mb-9"><Brand tagline /></div>
        {!token ? (
          <div>
            <h2 className="font-head text-3xl font-light tracking-tight text-slate-900">Link <span className="font-extrabold">not valid</span></h2>
            <p className="mt-2 text-sm text-slate-600" data-testid="reset-missing-token">This reset link is missing its token. Please request a new one from the sign-in screen.</p>
            <button data-testid="reset-to-signin" onClick={() => nav("/login")} className="dm-btn-primary mt-7 w-full !py-3">Back to sign in <ArrowRight size={16} /></button>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <CheckCircle2 size={28} className="dm-accent-text" />
            <h2 className="mt-4 font-head text-3xl font-light tracking-tight text-slate-900">Password <span className="font-extrabold">updated</span></h2>
            <p className="mt-2 text-sm text-slate-600">You can now sign in with your new password.</p>
            <button data-testid="reset-signin-btn" onClick={() => nav("/login")} className="dm-btn-primary mt-7 w-full !py-3">Sign in <ArrowRight size={16} /></button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p className="label-xs">Reset password</p>
            <h2 className="mt-2 font-head text-3xl font-light tracking-tight text-slate-900">Choose a new <span className="font-extrabold">password</span></h2>
            <p className="mt-2 text-sm text-slate-600">This link works once and expires an hour after it was sent.</p>
            <div className="mt-7 space-y-4">
              <div>
                <p className="label-xs mb-1.5">New password</p>
                <input type="password" data-testid="new-password-input" className="dm-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <p className="label-xs mb-1.5">Confirm new password</p>
                <input type="password" data-testid="confirm-password-input" className="dm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
            </div>
            {error && <p data-testid="reset-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
            <button type="submit" disabled={busy} data-testid="reset-submit" className="dm-btn-primary mt-7 w-full !py-3 disabled:opacity-60">
              {busy ? "Saving…" : "Save new password"} <ArrowRight size={16} />
            </button>
            <button type="button" data-testid="reset-cancel" onClick={() => nav("/login")} className="dm-btn-quiet mt-2 w-full">Back to sign in</button>
          </form>
        )}
      </div>
    </div>
  );
}
